import { useTranslation } from 'react-i18next';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { RemoteFile } from '../files';
import RemoteGallery from '../files/RemoteGallery';
import { Page } from './ui';
import { DateService } from '../common/date.service';
import { useLocation, useNavigate } from 'react-router-dom';
import { uploadHairLossPhotosHash } from './constants';
import TimelineArrow from './TimelineArrow';
import { CustomProperties } from '../events/analytics-event.dto';

type Props = {
  existingPhotos: RemoteFile[];
  sendAnalyticsEvent: (eventName: string, eventProperties: CustomProperties, userProperties: CustomProperties) => void;
};

const TrackYourProgress = ({ existingPhotos, sendAnalyticsEvent }: Props) => {
  const { pathname, search } = useLocation();
  const { t } = useTranslation('photosPage');
  const navigate = useNavigate();

  const groups: { [createdDate: string]: RemoteFile[] } = existingPhotos
    .filter((x) => x.type === 'other')
    .reduce(function (r, a) {
      const date = DateService.fromSQL(a.date).toFormat(DateService.shortDatabaseFormat);
      r[date] = r[date] || [];
      r[date].push(a);
      return r;
    }, Object.create(null));

  const sortedKeys = Object.keys(groups).sort((a, b) =>
    DateService.descending(DateService.fromShortSQL(a), DateService.fromShortSQL(b)),
  );

  const handleAddProgressPhotosClick = () => {
    sendAnalyticsEvent(
      'Clicked Element Add Progress Photos',
      { element_type: 'button', CTA_name: 'Add Progress Photos' },
      {},
    );
    navigate(`${pathname}${search}${uploadHairLossPhotosHash}`);
  };

  return (
    <Page>
      <Page.Title>{t('trackYourProgress')}</Page.Title>
      <Page.Text>{t('uploadMonthlyPhotos')}</Page.Text>
      <Page.TrackYourProgressActions>
        <Page.Button onClick={() => handleAddProgressPhotosClick()}>
          <Page.Button.Custom className="tw-bg-secondary tw-border-secondary tw-p-4 tw-px-8 tw-font-semibold">
            {t('addProgressPhotos').toUpperCase()}
          </Page.Button.Custom>
        </Page.Button>
      </Page.TrackYourProgressActions>
      <Page.PhotosBox whiteBackground>
        <Page.UploadBox.Image>
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {sortedKeys.map((key, index) => {
              const createdDate = key;
              const photos = groups[key];
              return (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot sx={{ color: '#2c5466', backgroundColor: '#2c5466' }} />
                    <TimelineConnector sx={{ color: '#D5DDE0', backgroundColor: '#D5DDE0' }} />
                  </TimelineSeparator>
                  <TimelineContent sx={index !== sortedKeys.length - 1 ? { paddingBottom: '40px' } : undefined}>
                    <>
                      <TimelineArrow text={DateService.fromShortSQL(createdDate).toFormat('LLL dd, yyyy')} />
                      <RemoteGallery photos={photos} />
                    </>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        </Page.UploadBox.Image>
      </Page.PhotosBox>
    </Page>
  );
};
export default TrackYourProgress;
